import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNotify, useRedirect } from 'react-admin';
import {
  createCaseFromIntake,
  dismissIntake,
  fetchLinkSuggestions,
  linkIntake,
  searchLinkTargets,
} from './api';
import { DISMISS_REASONS, dismissReady, formatCallTime, linkTargetLabel, placeLabel } from './kewixFormat';
import { DismissReason, IntakeFacts, LinkKind, LinkSearchResult, LinkSuggestion, TransportItem } from './types';

const SEARCH_DEBOUNCE_MS = 300;

const REASON_SX = (strong: boolean) => ({
  fontSize: 11.5,
  px: 0.75,
  borderRadius: 999,
  color: strong ? 'success.dark' : 'warning.dark',
  bgcolor: strong ? 'rgba(22,163,74,0.10)' : 'rgba(245,158,11,0.14)',
});

type Target = { kind: LinkKind; id: number | string };
const sameTarget = (a: Target | null, b: Target) => !!a && a.kind === b.kind && String(a.id) === String(b.id);

const KindTag = ({ kind }: { kind: LinkKind }) => (
  <Box
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      px: 0.75,
      mr: 1,
      borderRadius: 1,
      bgcolor: 'action.hover',
      color: 'text.secondary',
    }}
  >
    {kind === 'funeral' ? 'Case' : 'Lead'}
  </Box>
);

/**
 * Link a Kewix call to the case or lead it belongs to — the SIED "Assign to
 * case" dialog, for answering-service intakes. Ranked suggestions on top, search
 * below; when it truly isn't in our system, create a case from the call or
 * dismiss it with a reason.
 */
export const KewixLinkDialog = ({
  item,
  onClose,
  onChanged,
  onDismissed,
}: {
  item: TransportItem | null;
  onClose: () => void;
  onChanged: () => void;
  /** Called after a dismiss so the caller can offer Undo. */
  onDismissed: (item: TransportItem) => void;
}) => {
  const open = !!item;
  const notify = useNotify();
  const redirect = useRedirect();

  const [intake, setIntake] = useState<IntakeFacts | null>(null);
  const [suggestions, setSuggestions] = useState<LinkSuggestion[]>([]);
  const [results, setResults] = useState<LinkSearchResult[]>([]);
  const [selected, setSelected] = useState<Target | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<'link' | 'dismiss'>('link');
  const [reason, setReason] = useState<DismissReason | ''>('');
  const [note, setNote] = useState('');
  const requestId = useRef(0);

  // Every open starts clean.
  useEffect(() => {
    if (!open) return;
    setIntake(null);
    setSuggestions([]);
    setResults([]);
    setSelected(null);
    setSearch('');
    setDebounced('');
    setStep('link');
    setReason('');
    setNote('');
  }, [open, item?.id]);

  useEffect(() => {
    if (!item) return;
    let live = true;
    void fetchLinkSuggestions(item.id)
      .then((res) => {
        if (!live) return;
        setIntake(res.intake);
        setSuggestions(res.suggestions);
        if (res.suggestions.length) setSelected((cur) => cur ?? res.suggestions[0]);
      })
      .catch(() => live && setSuggestions([]));
    return () => {
      live = false;
    };
  }, [item]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open || !debounced) {
      setResults([]);
      return;
    }
    const current = ++requestId.current;
    setSearching(true);
    void searchLinkTargets(debounced)
      .then((r) => current === requestId.current && setResults(r))
      .catch(() => current === requestId.current && setResults([]))
      .finally(() => current === requestId.current && setSearching(false));
  }, [open, debounced]);

  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true);
    try {
      await fn();
    } catch {
      notify(failure, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleLink = () =>
    run(async () => {
      if (!item || !selected) return;
      await linkIntake(
        item.id,
        selected.kind === 'funeral' ? { funeralId: Number(selected.id) } : { leadId: String(selected.id) },
      );
      notify(`Kewix call linked to ${linkTargetLabel(selected.kind, selected.id)}`, { type: 'success' });
      onChanged();
      onClose();
    }, "Couldn't link this call");

  const handleCreate = () =>
    run(async () => {
      if (!item) return;
      const { funeralId } = await createCaseFromIntake(item.id);
      notify(`Case #${funeralId} created from the Kewix call`, { type: 'success' });
      onChanged();
      onClose();
      redirect('show', 'funerals', funeralId);
    }, "Couldn't create a case from this call");

  const handleDismiss = () =>
    run(async () => {
      if (!item || !reason) return;
      const res = await dismissIntake(item.id, reason, note.trim());
      if (res.result === 'already_dispatched') {
        notify('Kewix already dispatched this one — not dismissed', { type: 'warning' });
      } else if (res.result === 'dismissed') {
        onDismissed(item);
      }
      onChanged();
      onClose();
    }, "Couldn't dismiss this call");

  const facts = intake ?? (item ? {
    deceasedName: item.deceasedName,
    callerName: item.callerName,
    callerRelationship: item.callerRelationship,
    placeName: placeLabel(item.placeType, item.placeName),
    createdAt: item.createdAt,
  } : null);

  const row = (t: LinkSearchResult | LinkSuggestion, extra?: React.ReactNode) => {
    const isSel = sameTarget(selected, t);
    return (
      <Box
        key={`${t.kind}:${t.id}`}
        role="button"
        tabIndex={0}
        onClick={() => setSelected({ kind: t.kind, id: t.id })}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelected({ kind: t.kind, id: t.id })}
        sx={{
          border: 1,
          borderColor: isSel ? 'primary.main' : 'divider',
          bgcolor: isSel ? 'action.selected' : 'transparent',
          borderRadius: 1.5,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          <KindTag kind={t.kind} />
          {t.kind === 'funeral' ? `#${t.id} · ` : ''}
          {t.label}
        </Typography>
        {t.sub && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t.sub}
          </Typography>
        )}
        {extra}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {step === 'link' ? 'Link Kewix call to a case' : 'Not a case — dismiss'}
        {facts && (
          <Typography variant="body2" color="text.secondary">
            {formatCallTime(facts.createdAt)} · <strong>{facts.deceasedName || 'Deceased not named'}</strong>
            {facts.placeName ? ` · ${facts.placeName}` : ''}
            {facts.callerName || facts.callerRelationship
              ? ` · caller ${[facts.callerName, facts.callerRelationship].filter(Boolean).join(', ')}`
              : ''}
          </Typography>
        )}
      </DialogTitle>

      {step === 'link' ? (
        <DialogContent>
          {suggestions.length > 0 && (
            <Box mb={1.5}>
              <Typography variant="overline" color="text.secondary">
                Suggested
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {suggestions.map((s) =>
                  row(
                    s,
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {s.reasons.map((r) => (
                        <Box key={r.code} component="span" sx={REASON_SX(r.strong)}>
                          {r.label}
                          {r.strong ? ' ✓' : ''}
                        </Box>
                      ))}
                    </Box>,
                  ),
                )}
              </Box>
            </Box>
          )}
          <TextField
            id="kewix-link-search"
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            placeholder="Search cases and leads"
            helperText="Deceased or family name, phone, or case number (898)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
          />
          {debounced && !searching && results.length === 0 && (
            <Typography variant="body2" color="text.secondary" py={1.5}>
              Nothing matches &quot;{debounced}&quot;.
            </Typography>
          )}
          {results.length > 0 && (
            <Box display="flex" flexDirection="column" gap={1} mt={1}>
              {results.map((r) => row(r))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Not in our system?
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button size="small" variant="outlined" disabled={busy} onClick={() => void handleCreate()}>
              Create case from this call
            </Button>
            <Button size="small" color="inherit" disabled={busy} onClick={() => setStep('dismiss')}>
              Not a case — dismiss
            </Button>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography variant="body2" paragraph>
            Kewix will stop showing this call and won&apos;t dispatch transport for it. It&apos;s kept on
            record, and you can undo right after.
          </Typography>
          <TextField
            id="kewix-dismiss-reason"
            select
            fullWidth
            size="small"
            margin="dense"
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as DismissReason)}
          >
            {DISMISS_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="kewix-dismiss-note"
            fullWidth
            size="small"
            margin="dense"
            label={reason === 'other' ? 'Note (required)' : 'Note (optional)'}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
      )}

      <DialogActions>
        {step === 'link' ? (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => void handleLink()}
              disabled={!selected || busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {selected ? `Link to ${linkTargetLabel(selected.kind, selected.id)}` : 'Select a case or lead'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep('link')} disabled={busy}>
              Back
            </Button>
            <Button
              variant="contained"
              color="inherit"
              disableElevation
              onClick={() => void handleDismiss()}
              disabled={!dismissReady(reason, note) || busy}
            >
              Dismiss
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
